import type { JSX } from "react";
import { cn } from "./cn";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

export type EventBadgeColor = "amber" | "blue" | "green" | "purple" | "accent";

export interface EventCardProps {
  /** Leading icon name */
  readonly icon: IconName;
  /** Icon color class */
  readonly iconColor?: string;
  /** Event title */
  readonly title: string;
  /** Subtitle (time, location) */
  readonly subtitle: string;
  /** Date badge text (e.g. "Feb 14") */
  readonly dateBadge?: string;
  /** Date badge color */
  readonly dateBadgeColor?: EventBadgeColor;
  /** Click handler */
  readonly onClick?: () => void;
  /** Extra className */
  readonly className?: string;
}

const badgeColorMap: Readonly<Record<EventBadgeColor, { bg: string; text: string }>> = {
  // Keep compatibility with existing variants, but funnel to a quieter two-accent palette.
  amber: { bg: "bg-accent-subtle", text: "text-accent" },
  accent: { bg: "bg-accent-subtle", text: "text-accent" },
  blue: { bg: "bg-blue/12", text: "text-blue" },
  green: { bg: "bg-blue/12", text: "text-blue" },
  purple: { bg: "bg-blue/12", text: "text-blue" },
};

/**
 * Compact event card with icon, title, date badge on right, and subtitle.
 * Matches B1 Pencil design — used for Meetings section.
 */
export function EventCard({
  icon,
  iconColor = "text-content-secondary",
  title,
  subtitle,
  dateBadge,
  dateBadgeColor = "amber",
  onClick,
  className,
}: EventCardProps): JSX.Element {
  const Component = onClick ? "button" : "div";
  const colors = badgeColorMap[dateBadgeColor];
  return (
    <Component
      type={onClick ? "button" : undefined}
      className={cn(
        "flex flex-col gap-1 bg-surface-secondary rounded-[10px] px-4 py-3",
        "border border-edge",
        onClick && "cursor-pointer hover:bg-surface-hover transition-colors text-left w-full",
        className,
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={cn("shrink-0", iconColor)}>
          <Icon name={icon} size={16} />
        </span>
        <span className="flex-1 text-[13px] font-medium text-content truncate">{title}</span>
        {dateBadge ? (
          <span
            className={cn(
              "shrink-0 px-2 py-[3px] rounded-md text-[10px] font-semibold",
              colors.bg,
              colors.text,
            )}
          >
            {dateBadge}
          </span>
        ) : null}
      </div>
      <span className="text-xs text-content-secondary">{subtitle}</span>
    </Component>
  );
}
